import { AgentConversationsActions } from '@/agent/conversations/store/slice'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { ViewerDocumentKind, ViewerOpenContextKind, type ContentViewerSession } from '@/content-viewer/store/schemas'
import { getContentViewerCapabilities } from '@/content-viewer/utils/get-content-viewer-capabilities'
import { selectActiveContentViewerSession } from '@/content-viewer/store/selectors'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { ContractAlignRight, FileDownload, Folder, OpenNew } from '@/assets/icons'
import { RootState } from '@/store/store'
import handleContentViewerDownload from '@/content-viewer/util/handle-content-viewer-download'
import { ReactNode, useState } from 'react'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import clsx from 'clsx'
import { trackRiaSourceDownload, trackRiaSourceOpenInNewTabClicked, RiaSourceContext, createRiaContext } from '@/analytics/utils/ria-source-analytics'
import type { AnalyticsReference } from '@/analytics/schemas/analytics-reference.schemas'
import { RiaSourceTriggerLocation } from '@/analytics/enums/ria.enums'
import { selectAgentConversationMatterId } from '@/agent/conversations/store/selectors'
import { resolveStandaloneViewerUrlFromSession } from '@/standalone-reference-viewer/utils/resolve-standalone-viewer-url'
import { Link } from 'react-router-dom'
import { selectMatterById } from '@/matter/store/selectors'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { MenuContent, MenuItemGroup } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { SaveToDriveModal } from './save-to-drive-modal/SaveToDriveModalLayout'
import { generateDefaultFilename } from '@/artifacts/util/generate-default-filename'

export type ContentViewerHeaderProps = {
  conversationId?: string
  title: string
  loading: boolean
  isExporting?: boolean
  /** When set, the Download action for artifacts calls this instead of the default download. */
  onDownloadWithTemplate?: () => void
  /** When set, called when the user clicks "Save copy to matter files". */
  onSave?: () => void
  badge?: ReactNode
}

export default function ContentViewerHeader({ conversationId, title, loading, isExporting, onDownloadWithTemplate, onSave, badge }: ContentViewerHeaderProps) {
  const dispatch = useAppDispatch()

  const { trackEvent } = useAnalytics()
  const newFilesUxEnabled = kGetFeatureFlagStatus(kFeatureFlags.NEW_FILES_UX)

  const [downloadLoading, setDownloadLoading] = useState(false)
  const [saveToDriveLoading, setSaveToDriveLoading] = useState(false)
  const [saveToDriveModalOpen, setSaveToDriveModalOpen] = useState(false)
  const [artifactVersionIdForSave, setArtifactVersionIdForSave] = useState<string | null>(null)
  const [defaultFilenameForSave, setDefaultFilenameForSave] = useState<string | null>(null)

  const contentViewerSession = useAppSelector(selectActiveContentViewerSession)
  const sessionCapabilities = getContentViewerCapabilities(contentViewerSession)
  const isDownloadSupported = sessionCapabilities.canDownload

  const sessionCitationContext = contentViewerSession?.context.kind === ViewerOpenContextKind.CHAT_CITATION ? contentViewerSession.context : null
  const sessionCitationMatterId = useAppSelector((state: RootState) =>
    sessionCitationContext ? selectAgentConversationMatterId(state, { conversationId: sessionCitationContext.conversationId }) : null
  )
  const sessionRiaContext =
    sessionCitationContext && createRiaContext(sessionCitationContext.conversationId, sessionCitationContext.turnId, sessionCitationMatterId)
  const standaloneViewerUrl = resolveStandaloneViewerUrlFromSession(contentViewerSession, sessionRiaContext || undefined)
  const showStandaloneViewerButton = !loading && sessionCapabilities.canOpenInNewTab && !!standaloneViewerUrl

  //Find if the current content matter id is an unassigned matter to prevent saving to drive for unassigned matters.
  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const activeMatter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: activeMatterId }))
  const isActiveMatterUnassigned = isUnassignedMatter(activeMatter)

  const handleSaveToDriveClick = async () => {
    if (contentViewerSession?.document.kind !== ViewerDocumentKind.ARTIFACT) return

    setSaveToDriveLoading(true)
    setArtifactVersionIdForSave(contentViewerSession.document.artifactVersionId)
    setDefaultFilenameForSave(generateDefaultFilename(contentViewerSession.document.name))
    setSaveToDriveModalOpen(true)
    setSaveToDriveLoading(false)
  }

  const handleClose = () => {
    // Clear the content viewer
    dispatch(ContentViewerActions.clearContentViewer())

    // Remove the active content viewer for the conversation
    if (conversationId) {
      dispatch(AgentConversationsActions.removeConversationActiveContentViewer({ conversationId }))
    }
  }

  const maybeTrackRiaSourceSessionDownload = (session: ContentViewerSession, context: RiaSourceContext | null | undefined) => {
    if (!context) return
    // RIA "source download" historically counts caselaw/LRR rich references only. File-backed citations
    // (user-file/legacy-file) are downloadable but are not RIA source downloads. Open-in-new-tab still
    // fires for all citation kinds via buildAnalyticsReferenceFromSession; keep this gate download-only.
    if (session.document.kind !== ViewerDocumentKind.CASELAW && session.document.kind !== ViewerDocumentKind.LRR) return
    const reference = buildAnalyticsReferenceFromSession(session)
    if (!reference) return

    trackRiaSourceDownload({
      trackEvent,
      context,
      reference,
      triggerLocation: RiaSourceTriggerLocation.ViewerHeaderButton,
    })
  }

  const handleDownload = async () => {
    if (!contentViewerSession) return

    if (onDownloadWithTemplate && contentViewerSession.document.kind === ViewerDocumentKind.ARTIFACT) {
      onDownloadWithTemplate()
      return
    }

    maybeTrackRiaSourceSessionDownload(contentViewerSession, sessionRiaContext)
    setDownloadLoading(true)
    await handleContentViewerDownload(contentViewerSession, trackEvent)
    setDownloadLoading(false)
  }

  const handleOpenInNewTab = () => {
    if (!contentViewerSession) return
    const reference = buildAnalyticsReferenceFromSession(contentViewerSession)
    if (!reference || !sessionRiaContext) return

    trackRiaSourceOpenInNewTabClicked({
      trackEvent,
      context: sessionRiaContext,
      reference,
      triggerLocation: RiaSourceTriggerLocation.ContentViewerHeader,
    })
  }

  return (
    <>
      {!newFilesUxEnabled && (
        <SaveToDriveModal
          open={saveToDriveModalOpen}
          onClose={() => setSaveToDriveModalOpen(false)}
          artifactVersionId={artifactVersionIdForSave}
          defaultFilename={defaultFilenameForSave}
          matterId={activeMatterId ?? ''}
        />
      )}
      <div
        className={clsx(
          'grid grid-cols-[26px_auto_84px] items-center px-3 border-b box-content sticky top-0 bg-brand-neutral-50',
          badge ? 'min-h-[60px]' : 'h-[60px]'
        )}
      >
        <div className="w-full text-center flex items-center justify-center">
          <button
            type="button"
            className="text-brand-neutral-600 bg-brand-neutral-100 border border-brand-neutral-200 rounded-[4px] focus:outline-none hocus:bg-brand-neutral-200"
            onClick={handleClose}
            aria-label="Close Artifact Viewer"
          >
            <span className="sr-only">Close sidebar</span>
            <ContractAlignRight className="h-[14px] w-[14px] m-1" aria-hidden="true" />
          </button>
        </div>
        {badge ? (
          <div className="ml-4 flex flex-col gap-0.5 min-w-0 pt-4 pb-6">
            <div className="font-bold text-base overflow-hidden text-ellipsis whitespace-nowrap pr-1" title={title}>
              {title}
            </div>
            <div className="self-start">{badge}</div>
          </div>
        ) : (
          <div className="font-bold ml-4 text-base overflow-hidden text-ellipsis whitespace-nowrap pr-1" title={title}>
            {title}
          </div>
        )}
        {/* Only Download available for non-artifact content */}
        <div className="flex items-center justify-end gap-1 h-10">
          {!sessionCapabilities.canSaveToDrive || isActiveMatterUnassigned ? (
            <>
              {(loading || downloadLoading || isExporting) && <CircularProgressContinuousSized size={16} thickness={7} />}
              {!loading && !downloadLoading && !isExporting && isDownloadSupported && (
                <button
                  type="button"
                  className="text-brand-neutral-600 rounded-full focus:outline-none hocus:bg-brand-neutral-200 h-8 w-8 flex items-center justify-center"
                  aria-label="Download"
                  onClick={handleDownload}
                >
                  <FileDownload className="h-6 w-6" />
                </button>
              )}
            </>
          ) : (
            <>
              {/* Download and Save copy to Drive options */}
              {(loading || downloadLoading || isExporting || saveToDriveLoading) && <CircularProgressContinuousSized size={16} thickness={7} />}
              {!loading && !downloadLoading && !isExporting && !saveToDriveLoading && isDownloadSupported && (
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <button
                      type="button"
                      className="text-brand-neutral-600 rounded-full focus:outline-none hocus:bg-brand-neutral-200 h-8 w-8 flex items-center justify-center"
                      aria-label="Download or Save Options"
                    >
                      <FileDownload className="h-6 w-6" />
                    </button>
                  </DropdownMenu.Trigger>

                  <MenuContent className="min-w-[186px]" sideOffset={5} align="end">
                    <MenuItemGroup>
                      <MenuItem label="Download" icon={FileDownload} onSelect={handleDownload} variant="neutral" />
                      <MenuItem
                        label="Save copy to matter files"
                        icon={Folder}
                        onSelect={() => (newFilesUxEnabled ? onSave?.() : handleSaveToDriveClick())}
                        disabled={!activeMatterId}
                        variant="neutral"
                      />
                    </MenuItemGroup>
                  </MenuContent>
                </DropdownMenu.Root>
              )}
            </>
          )}

          {showStandaloneViewerButton && (
            <Link
              to={standaloneViewerUrl}
              target="_blank"
              className="text-brand-neutral-600 rounded-full focus:outline-none hocus:bg-brand-neutral-200 h-8 w-8 flex items-center justify-center"
              aria-label="Open in new tab"
              onClick={handleOpenInNewTab}
            >
              <OpenNew className="h-6 w-6" />
            </Link>
          )}
        </div>
      </div>
    </>
  )
}

function buildAnalyticsReferenceFromSession(session: ContentViewerSession): AnalyticsReference | null {
  if (session.context.kind !== ViewerOpenContextKind.CHAT_CITATION) return null
  return session.context.analyticsReference ?? null
}
